import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Input, InputNumber, Switch, Drawer, Select, Radio } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { useMercuryContext } from "@context/index";
import { successfulNotification, failedNotification } from "common/utils/notifications";
import { handleApiError } from "common/utils/handleApiError";
import { applyRestValidationError, applyRestDuplicateKeyError } from "common/utils/validationErrors";
import { useDrawerScrollMargin } from "common/utils/useScrollPosition";
import { fetchParentDepartments } from "./EmpowerDepartment.Filter";
import { targetEntityOptions } from "filters/TargetEntity.filter";

type Mode = "create" | "edit" | "delete" | "restore";

export interface LineItemDefinition {
  id?: string;
  name: string;
  description?: string;
  category?: string;
  rateType?: "wordRate" | "serviceRate" | "dtpRate" | "engRate";
  varServiceType: "TRANSLATION" | "AUDIT" | "LQA" | "PLATFORM_PROVISION" | "PREPROCESSING" | "POSTPROCESSING" | "OTHER";
  serviceTypeId: string;
  invoiceTypes?: string[];
  quantitySource?: "fieldPath" | "fixed";
  quantityTargetEntity?: "DOCUMENT" | "PLP" | "PROJECT" | "ENTITY";
  quantityFieldPath?: string | null;
  quantityFieldPathId?: string | null;
  fixedQuantity?: number;
  active: boolean;
  deleted: boolean;
  empowerDepartment: string;
}

interface InvoiceLineItemFormProps {
  mode?: Mode;
  editData?: LineItemDefinition | null;
  refreshList?: () => void;
  disabled?: boolean;
}

const actionText: Record<Mode, string> = {
  create: "Create",
  edit: "Edit",
  delete: "Delete",
  restore: "Restore",
};

const InvoiceLineItemForm: React.FC<InvoiceLineItemFormProps> = ({
  mode = "create",
  editData,
  refreshList,
  disabled = false,
}) => {
  const context = useMercuryContext();
  const { invoicingClient, gpClient } = context;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState<{ _id: string; name: string }[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [departments, setDepartments] = useState<{ _id: string; departmentName: string }[]>([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [worklogDefinitions, setWorklogDefinitions] = useState<
    { _id: string; name: string }[]
  >([]);
  const [worklogDefinitionsLoading, setWorklogDefinitionsLoading] =
    useState(false);
  const [form] = Form.useForm<LineItemDefinition>();
  const targetEntityWatch = Form.useWatch("quantityTargetEntity", form);
  const isDrawerMode = mode === "edit" || mode === "create";

  useDrawerScrollMargin("", open);

  useEffect(() => {
    if (!open) return;

    const fetchServices = async () => {
      try {
        setServicesLoading(true);
        const res = await gpClient.post(`google-gp-v1/service/search`, { filter: { deleted: { $nw: false } } });
        setServices(res.data?.documents || []);
      } catch (error) {
        handleApiError(error, "Failed to load services");
      } finally {
        setServicesLoading(false);
      }
    };
    fetchServices();

    const fetchDepartments = async () => {
      try {
        setDepartmentsLoading(true);
        const result = await fetchParentDepartments(context);
        setDepartments(result);
      } catch (error) {
        handleApiError(error, "Failed to load departments");
      } finally {
        setDepartmentsLoading(false);
      }
    };
    fetchDepartments();

    if (editData) {
      form.setFieldsValue({
        ...editData,
        active: editData.active ?? true,
        quantitySource: editData.quantitySource ?? "fixed",
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ active: true, quantitySource: "fixed" });
    }
  }, [open, editData, form]);

  useEffect(() => {
    if (!open) {
      setWorklogDefinitions([]);
      return;
    }
    if (targetEntityWatch !== "PLP" && targetEntityWatch !== "DOCUMENT") {
      setWorklogDefinitions([]);
      return;
    }
    let cancelled = false;
    const fetchDefinitions = async () => {
      try {
        setWorklogDefinitionsLoading(true);
        const res = await invoicingClient.post(
          "/invoicing-v1/worklog-type-definitions/search",
          {
            filter: {
              deleted: false,
              targetEntity: targetEntityWatch,
            },
            sort: { name: 1 },
            pagination: { pageNumber: 1, resultsPerPage: 1000 },
          }
        );
        if (cancelled) return;
        setWorklogDefinitions(res.data?.documents ?? []);
      } catch (error) {
        if (!cancelled) {
          handleApiError(error, "Failed to load worklog definitions");
          setWorklogDefinitions([]);
        }
      } finally {
        if (!cancelled) setWorklogDefinitionsLoading(false);
      }
    };
    fetchDefinitions();
    return () => {
      cancelled = true;
    };
  }, [open, targetEntityWatch, invoicingClient]);

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    form.resetFields();
  };

  const handleSubmit = async (values: LineItemDefinition) => {
    values.name = values.name?.trim();
    values.description = values.description?.trim();
    values.category = values.category?.trim();
    if (values.quantitySource === "fieldPath") {
      const trimmedPath = values.quantityFieldPath?.trim() || null;
      if (values.quantityFieldPathId) {
        values.quantityFieldPath = null;
      } else {
        values.quantityFieldPath = trimmedPath;
        values.quantityFieldPathId = null;
      }
    } else {
      values.quantityFieldPath = null;
      values.quantityFieldPathId = null;
    }

    try {
      setLoading(true);

      if ((mode === "delete" || mode === "restore") && editData?.id) {
        await invoicingClient.patch(
          `/invoicing-v1/line-item-definitions/${editData.id}`,
          {
            deleted: mode === "delete",
          }
        );

        successfulNotification(
          mode === "delete"
            ? "Line item definition deleted"
            : "Line item definition restored"
        );
      } else if (editData?.id) {
        await invoicingClient.patch(
          `/invoicing-v1/line-item-definitions/${editData.id}`,
          values
        );
        successfulNotification("Line item definition updated");
      } else {
        const payload = {
          ...values,
          deleted: false,
        };
        await invoicingClient.post(
          `/invoicing-v1/line-item-definitions`,
          payload
        );
        successfulNotification("Line item definition created");
      }

      refreshList?.();
      handleClose();
    } catch (error) {
      const formLevel = applyRestValidationError(error, values, form);
      if (formLevel !== null) {
        if (formLevel.length) failedNotification("Error", formLevel.join(" "));
        return; // handled
      }
      if (applyRestDuplicateKeyError(error, form, { noun: "line item definition" })) {
        return; // handled (inline on the duplicated field, or toast)
      }
      handleApiError(error, "Failed to save line item definition");
    } finally {
      setLoading(false);
    }
  };

  const handleFloatInput = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      !/[0-9.]/.test(event.key) &&
      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(
        event.key
      )
    ) {
      event.preventDefault();
    }
  };

  const InvoiceLineItemFormContent = (
    <Form<LineItemDefinition>
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{ active: true, quantitySource: "fixed" }}
    >
      <Form.Item
        name="name"
        label="Name"
        rules={[{ required: true, message: "Name is required" }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="serviceTypeId"
        label="Service"
        rules={[{ required: true, message: "Service is required" }]}
      >
        <Select
          loading={servicesLoading}
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Select a service"
          options={services.map((service) => ({
            value: service._id,
            label: service.name,
          }))}
        />
      </Form.Item>

      <Form.Item name="description" label="Description">
        <Input.TextArea rows={2} />
      </Form.Item>

      <Form.Item name="category" label="Category">
        <Input />
      </Form.Item>
      

      <Form.Item
        name="varServiceType"
        label="VAR Service Type"
        rules={[{ required: true, message: "VAR service type is required" }]}
      >
        <Select
          placeholder="Select a VAR service type"
          options={[
            { value: "TRANSLATION", label: "Translation" },
            { value: "AUDIT", label: "Audit" },
            { value: "LQA", label: "LQA" },
            { value: "PLATFORM_PROVISION", label: "Platform Provision" },
            { value: "PREPROCESSING", label: "Preprocessing" },
            { value: "POSTPROCESSING", label: "Postprocessing" },
            { value: "OTHER", label: "Other" },
          ]}
        />
      </Form.Item>

      <Form.Item name="rateType" label="Rate Type">
        <Select
          allowClear
          placeholder="Select a rate type"
          options={[
            { value: "wordRate", label: "Word Rate" },
            { value: "serviceRate", label: "Service Rate" },
            { value: "dtpRate", label: "DTP Rate" },
            { value: "engRate", label: "Engineering Rate" },
          ]}
        />
      </Form.Item>

      <Form.Item
        name="empowerDepartment"
        label="Empower Department"
        rules={[{ required: true, message: "Empower Department is required" }]}
      >
        <Select
          loading={departmentsLoading}
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Select an Empower Department"
          options={departments.map((dept) => ({
            value: dept._id,
            label: dept.departmentName,
          }))}
        />
      </Form.Item>

      <Form.Item
        name="quantitySource"
        label="Quantity Source"
        rules={[{ required: true, message: "Quantity source is required" }]}
      >
        <Radio.Group>
          <Radio value="fixed">Fixed</Radio>
          <Radio value="fieldPath">DB Field Path</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prev, curr) =>
          prev.quantitySource !== curr.quantitySource
        }
      >
        {({ getFieldValue }) =>
          getFieldValue("quantitySource") === "fixed" ? (
            <Form.Item
              name="fixedQuantity"
              label="Fixed Quantity"
              rules={[
                { required: true, message: "Fixed quantity is required" },
                { type: "number", min: 0 },
              ]}
            >
              <InputNumber
                onKeyDown={handleFloatInput}
                style={{ width: "100%" }}
              />
            </Form.Item>
          ) : (
            <>
              <Form.Item
                name="quantityTargetEntity"
                label="Quantity Target Entity"
                rules={[
                  { required: true, message: "Target entity is required" },
                ]}
              >
                <Select
                  placeholder="Select target entity"
                  options={targetEntityOptions}
                  onChange={() => {
                    form.setFieldsValue({ quantityFieldPathId: null });
                  }}
                />
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) =>
                  prev.quantityFieldPath !== curr.quantityFieldPath ||
                  prev.quantityFieldPathId !== curr.quantityFieldPathId ||
                  prev.quantityTargetEntity !== curr.quantityTargetEntity
                }
              >
                {({ getFieldValue }) => {
                  const fieldPath = getFieldValue("quantityFieldPath");
                  const fieldPathId = getFieldValue("quantityFieldPathId");
                  const targetEntity = getFieldValue("quantityTargetEntity");
                  const definitionsAvailable =
                    targetEntity === "PLP" || targetEntity === "DOCUMENT";
                  return (
                    <>
                      <Form.Item
                        name="quantityFieldPathId"
                        label="Worklog Definition (optional)"
                        dependencies={["quantityFieldPath"]}
                      >
                        <Select
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          placeholder={
                            definitionsAvailable
                              ? "Select a worklog definition"
                              : "Only available for PLP or Document target entities"
                          }
                          loading={worklogDefinitionsLoading}
                          disabled={!definitionsAvailable || !!fieldPath}
                          options={worklogDefinitions.map((def) => ({
                            value: def._id,
                            label: def.name,
                          }))}
                        />
                      </Form.Item>
                      <Form.Item
                        name="quantityFieldPath"
                        label="Quantity Field Path"
                        dependencies={["quantityFieldPathId"]}
                        rules={[
                          {
                            validator: (_rule, value) => {
                              const trimmed = value?.trim();
                              if (!trimmed && !fieldPathId) {
                                return Promise.reject(
                                  new Error(
                                    "Provide either a worklog definition or a field path"
                                  )
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input
                          placeholder="e.g. document.wordCount"
                          disabled={!!fieldPathId}
                        />
                      </Form.Item>
                    </>
                  );
                }}
              </Form.Item>
            </>
          )
        }
      </Form.Item>

      <Form.Item name="active" label="Active?" valuePropName="checked">
        <Switch defaultValue={true} />
      </Form.Item>
    </Form>
  );

  return (
    <>
      <Button
        icon={
          mode === "edit" ? (
            <EditOutlined />
          ) : mode === "delete" ? (
            <DeleteOutlined />
          ) : mode === "restore" ? (
            <UndoOutlined />
          ) : (
            <PlusOutlined />
          )
        }
        type={mode === "create" ? "primary" : "link"}
        danger={mode === "delete"}
        size={mode === "create" ? "middle" : "small"}
        onClick={handleOpen}
        disabled={disabled}
      >
        {mode === "create" ? "Add Line Item Definition" : actionText[mode]}
      </Button>

      {isDrawerMode && (
        <Drawer
          title={`${actionText[mode]} Line Item Definition`}
          open={open}
          onClose={handleClose}
          width={480}
          destroyOnClose
          maskClosable={!loading}
          closable={!loading}
          className="invoice-type-drawer"
          footer={
            <div style={{ marginBottom: 120 }}>
              <Button
                type="primary"
                loading={loading}
                onClick={() => form.submit()}
                style={{ marginLeft: 8 }}
              >
                {mode === "edit" ? "Save" : actionText[mode]} Line Item
                Definition
              </Button>
            </div>
          }
        >
          {InvoiceLineItemFormContent}
        </Drawer>
      )}

      {!isDrawerMode && (
        <Modal
          title={`${actionText[mode]} Line Item Definition`}
          open={open}
          onCancel={handleClose}
          onOk={() => handleSubmit({} as LineItemDefinition)}
          confirmLoading={loading}
          destroyOnClose
          okText={actionText[mode]}
          maskClosable={!loading}
          closable={!loading}
        >
          {mode === "delete"
            ? "Are you sure you want to delete this line item definition?"
            : "Are you sure you want to restore this line item definition?"}
        </Modal>
      )}
    </>
  );
};

export default InvoiceLineItemForm;
